import { get } from 'svelte/store'
import { pageOptsStore, accessTokenStore, clientPublicStore } from '@packages/stores'
import { requestReceipt } from './Payment.service'
import { attachMetadataToItems } from '@src/passport/blockchain'
import { configStore } from '@src/stores/config'
import { PassportEvents, dispatch } from '@lib/events/events'
import { getCompmletedOrderDetails } from '@lib/orders'
import { type TPaymentReturn } from '@packages/utils/types'
import type { TMembershipPaymentData, TBasicPaymentData, TTokenPaymentData, TLicensePaymentData } from './oders.types'
import { contractsStore } from '@src/stores'

export const createOrder = async (body: TBasicPaymentData): Promise<string> => {
  const paymentOpts = get(pageOptsStore)

  const licensesPaymentData: TLicensePaymentData[] =
    paymentOpts.licenses?.map(
      ({
        contentTokenId,
        licenseContractAddress,
        licenseType,
      }: {
        contentTokenId: number
        licenseContractAddress: string
        licenseType: number
      }) => ({
        contract_address: licenseContractAddress,
        content_token_id: +contentTokenId,
        license_type: +licenseType,
      }),
    ) || []

  const credAddress = get(contractsStore).erc20Cred.address
  const clientFee = get(clientPublicStore).client_payment_configuration?.token_fee
  const tokensPaymentData: TTokenPaymentData[] =
    paymentOpts.tokens?.map(
      ({ contractAddress, tokenId, amount }: { contractAddress: string; tokenId: number; amount: string | number }) => {
        if (credAddress === contractAddress && clientFee) {
          amount = +amount + +amount * clientFee
        }

        return {
          contract_address: contractAddress,
          token_id: +tokenId,
          amount,
        }
      },
    ) || []

  const membershipsPaymentData: TMembershipPaymentData[] =
    paymentOpts.memberships?.map(
      ({ contractAddress, membershipTokenId }: { contractAddress: string; membershipTokenId?: string }) => ({
        contract_address: contractAddress,
        ...(membershipTokenId ? { token_id: membershipTokenId } : {}),
      }),
    ) || []

  const payload = {
    ...body,
    tokens: tokensPaymentData,
    memberships: membershipsPaymentData,
    licenses: licensesPaymentData,
  }

  const config = get(configStore)
  const res = await fetch(config.credenza.evmApiUrl + '/contracts/buy-assets', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${get(accessTokenStore)}`,
    },
    method: 'POST',
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error('Failed to create order')
  const { _id }: { _id: string } = await res.json()
  return _id
}

export const processOrder = async (orderId: string): Promise<TPaymentReturn> => {
  const config = get(configStore)
  const paymentOpts = get(pageOptsStore)

  const data = (await getCompmletedOrderDetails(config.credenza.generalApiUrl, orderId)) as TPaymentReturn

  const paymentIntentId = data.payment_provider_tx
  if (paymentIntentId && data.payment_provider !== 'paystack' && data.status === 'COMPLETED') {
    await requestReceipt({
      paymentIntentId,
      title: paymentOpts.title,
      subtitle: paymentOpts.subtitle,
    })
    dispatch(PassportEvents.RECEIPT, { paymentIntentId })
  }

  await attachMetadataToItems(data.items)
  return data
}
